import { useCallback, useState } from 'react';
import { ContentPrivateRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { Color } from '@diritto/common/style/color';
import {
  ContentListType,
  TrackerExtraInfo,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { PersonalContentRelayDto } from '@diritto/common/relay/dtos/personal-relay.dtos';
import { useTrackingState } from 'contexts/TrackingProvider';
import { NotiIcon } from 'icons/NotiIcon';
import { IconButton } from 'ui-components/common/IconButton';
import { Link } from 'ui-components/common/Link';
import { showToast } from 'utils/modal';
import { useAddNewEpisodeNotiMutation } from 'queries/contentNoti/useAddNewEpisodeNotiMutation';
import { useDeleteNewEpisodeNotiMutation } from 'queries/contentNoti/useDeleteNewEpisodeNotiMutation';
import { InfoIcon } from 'icons/InfoIcon';
import { ContentPrivateCover } from 'ui-components/contents/ContentPrivateCover';
import { ContentPrivateItemMeta } from 'app-components/contents/ContentPrivateItemMeta';

interface FavoritePrivateContentItemProps {
  personalContent: PersonalContentRelayDto;
  content: ContentPrivateRelayDto;
  profile?: ProfileLitePublicRelayDto;
  listItemIndex?: number;
}

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.My,
};

export const FavoritePrivateContentItem = ({
  personalContent,
  content,
  profile,
  listItemIndex = 0,
}: FavoritePrivateContentItemProps) => {
  const { tracker } = useTrackingState();
  const handleClick = useCallback(() => {
    tracker.track(TrackingEventType.SelectContentListItem, {
      listType: ContentListType.Favorites,
      listItemIndex,
      contentId: content.id,
      trackerExtraInfo,
    });
  }, [content.id, listItemIndex, tracker]);
  const [isNoti, setIsNoti] = useState(personalContent.isNotiEnabled);

  const { mutate: addNewEpisodeNotiMutate } = useAddNewEpisodeNotiMutation(content.id, trackerExtraInfo, {
    onMutate: () => {
      setIsNoti(true);
    },
    onError: () => {
      setIsNoti(false);
    },
  });
  const { mutate: deleteNewEpisodeNotiMutate } = useDeleteNewEpisodeNotiMutation(content.id, trackerExtraInfo, {
    onMutate: () => {
      setIsNoti(false);
    },
    onError: () => {
      setIsNoti(true);
    },
  });

  const handlePressNoti = useCallback(() => {
    if (isNoti) {
      deleteNewEpisodeNotiMutate();
      showToast('회차 등록 알림이 해제되었어요.');
    } else {
      addNewEpisodeNotiMutate();
      showToast('회차 등록 알림이 설정되었어요.');
    }
  }, [addNewEpisodeNotiMutate, deleteNewEpisodeNotiMutate, isNoti]);

  return (
    <div className="flex flex-row w-full flex-1">
      <div className="flex flex-row w-full items-center gap-4" onClick={handleClick}>
        <Link href={`/contents/${content.id}`}>
          <ContentPrivateCover size={90} content={content} />
        </Link>
        <div className="flex flex-col flex-1">
          <ContentPrivateItemMeta
            content={content}
            profile={profile}
            href={`/contents/${content.id}`}
            showCategoryType
          />
          <div className="flex flex-row w-full items-center mt-2 gap-[2px]">
            <InfoIcon className="size-[18px]" color={Color.Gray500} />
            <div className="justify-start items-center leading-none text-gray-500 text-xxxs font-medium">
              비공개 처리된 작품이에요.
            </div>
          </div>
        </div>
        <IconButton className="ml-auto w-6 h-6" onClick={handlePressNoti}>
          <NotiIcon
            fill={isNoti}
            color={isNoti ? Color.Primary500 : Color.Gray600}
            fillColor={isNoti ? Color.Primary500 : Color.Gray600}
          />
        </IconButton>
      </div>
    </div>
  );
};
